// Ported verbatim from miri-reactnative/components/WeightProgressCard.tsx —
// the weight chart on the multi-program SKU's Progress page. It depends
// only on published SDK exports (useWeightHistory, theme, icons) +
// react-native-svg, and its chart is static SVG paths (no reanimated), so
// it renders on web unchanged. If this card is promoted to the SDK,
// replace this file with the SDK import.
import {
  ArrowBottomIcon,
  ArrowTopIcon,
  theme,
  useTheme,
  useWeightHistory,
  type PlanArtifactWeight,
  type WeightHistoryPoint,
  type WeightTracking,
} from '@miri-ai/miri-react-native';
import { useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Path, Stop } from 'react-native-svg';

interface WeightProgressCardProps {
  weightTracking: WeightTracking | null;
  weightGoal: PlanArtifactWeight | null;
}

// Server stores units as full words (`pound`, `kilogram`); UI shows the
// 2-3 letter abbreviation. Pass-through anything else.
const UNIT_DISPLAY: Record<string, string> = {
  pound: 'lbs',
  pounds: 'lbs',
  lb: 'lbs',
  kilogram: 'kg',
  kilograms: 'kg',
};

function displayUnit(unit: string | undefined): string {
  if (!unit) return 'lbs';
  return UNIT_DISPLAY[unit.toLowerCase()] ?? unit;
}

/**
 * Normalize a unit string to its canonical short form for equality checks.
 * "pound" / "lb" / "lbs" all collapse to "lbs"; "kilogram" / "kg" → "kg".
 */
function canonicalUnit(unit: string | undefined): string {
  if (!unit) return 'lbs';
  return UNIT_DISPLAY[unit.toLowerCase()] ?? unit.toLowerCase();
}

const CHART_HEIGHT = 80;
const CHART_PAD_TOP = 8;
const CHART_PAD_BOTTOM = 16;
const CHART_PAD_X = 4;
const CHART_DAYS = 7;

/**
 * Build SVG paths for the line + filled area through history points, plus
 * a dashed projection segment from the last real data point to the goal
 * weight anchored at the chart's right edge.
 *
 * Returns null only when there are zero non-null points or zero width.
 * A single data point still renders — paired with the goal projection it
 * visualizes the user's starting position vs. their target.
 *
 * `points` is an N-day history (typically 7) with `null` for missing days
 * so the X-axis stays time-aligned. `goalValue` is included in the Y-range
 * only when non-null AND the unit matches the history's unit (caller
 * enforces).
 */
export function buildChartPaths(
  points: WeightHistoryPoint[],
  width: number,
  goalValue: number | null,
): {
  line: string;
  area: string;
  dots: { x: number; y: number }[];
  goalY: number | null;
  projection: string | null;
} | null {
  if (width <= 0) return null;
  const valid = points
    .map((p, i) => (p.value != null ? { i, v: p.value } : null))
    .filter((x): x is { i: number; v: number } => x !== null);
  if (valid.length < 1) return null;

  const values = valid.map((v) => v.v);
  if (goalValue != null) values.push(goalValue);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 0.5); // avoid divide-by-zero on flat series

  const innerW = width - 2 * CHART_PAD_X;
  const innerH = CHART_HEIGHT - CHART_PAD_TOP - CHART_PAD_BOTTOM;
  const xStep = innerW / Math.max(points.length - 1, 1);
  const yFor = (v: number): number =>
    CHART_PAD_TOP + (1 - (v - min) / span) * innerH;

  const coords = valid.map(({ i, v }) => ({
    x: CHART_PAD_X + i * xStep,
    y: yFor(v),
  }));

  // Line — degenerate to a horizontal stub on a single point so the dot
  // sits visibly on a tiny line stub rather than floating alone.
  const line =
    coords.length === 1
      ? `M${coords[0].x} ${coords[0].y} L${coords[0].x + 0.01} ${coords[0].y}`
      : coords
          .map((c, idx) => `${idx === 0 ? 'M' : 'L'}${c.x} ${c.y}`)
          .join(' ');

  const first = coords[0];
  const last = coords[coords.length - 1];
  const baselineY = CHART_PAD_TOP + innerH;
  const area = `M${first.x} ${baselineY} ${coords
    .map((c) => `L${c.x} ${c.y}`)
    .join(' ')} L${last.x} ${baselineY} Z`;

  const goalY = goalValue != null ? yFor(goalValue) : null;
  // Projection segment: dashed line from the user's most recent data point
  // to the goal weight at the chart's right edge. Visualizes "where you
  // are → where you're going" so the chart stays useful with sparse data.
  const projection =
    goalY != null
      ? `M${last.x} ${last.y} L${width - CHART_PAD_X} ${goalY}`
      : null;

  return {
    line,
    area,
    dots: coords,
    goalY,
    projection,
  };
}

/**
 * Build a screen-reader label summarizing the trend through the history.
 * Returns null only when there are zero non-null points.
 */
export function buildChartA11yLabel(
  points: WeightHistoryPoint[],
  unit: string,
): string | null {
  const values: number[] = [];
  for (const p of points) {
    if (p.value != null) values.push(p.value);
  }
  if (values.length < 1) return null;
  const u = displayUnit(unit);
  if (values.length === 1) {
    return `Current weight: ${values[0].toFixed(1)} ${u}`;
  }
  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  let direction: 'down' | 'up' | 'unchanged' = 'unchanged';
  if (delta < 0) direction = 'down';
  else if (delta > 0) direction = 'up';
  const magnitude = Math.abs(delta).toFixed(1);
  return `${values.length}-day weight trend: ${first.toFixed(1)} to ${last.toFixed(1)} ${u}, ${direction} ${magnitude} ${u}`;
}

/**
 * Weight Progress card on the Progress tab — current weight + goal + delta
 * this week. Per the GLP-1 prototype's WEIGHT card (ProgressScreen.jsx:46-100).
 *
 * MVP: renders the current/goal/delta numbers using existing
 * `weightTracking` data from `useOverview`. The 7-day area chart is a
 * follow-up — needs a per-day weight history endpoint that doesn't exist
 * client-side yet.
 *
 * Gated by the caller on `weight_management` focus via `useFocus()` — only
 * renders for users tracking weight as part of their program.
 */
export function WeightProgressCard({
  weightTracking,
  weightGoal,
}: WeightProgressCardProps) {
  const { colors } = useTheme();
  const { data: weightHistory } = useWeightHistory({ days: CHART_DAYS });
  const [chartWidth, setChartWidth] = useState(0);
  const handleChartLayout = (e: LayoutChangeEvent) => {
    setChartWidth(e.nativeEvent.layout.width);
  };

  const chartPaths = useMemo(() => {
    if (!weightHistory?.history) return null;
    // Only pass goalValue when the goal's unit matches the history's unit.
    // Mismatched units (kg goal vs lbs history) would land the dashed
    // reference line at the wrong numeric position.
    const unitsMatch =
      weightGoal != null &&
      canonicalUnit(weightGoal.unit) === canonicalUnit(weightHistory.unit);
    return buildChartPaths(
      weightHistory.history,
      chartWidth,
      unitsMatch ? (weightGoal?.value ?? null) : null,
    );
  }, [weightHistory, chartWidth, weightGoal]);

  const chartA11yLabel = useMemo(
    () =>
      weightHistory?.history
        ? buildChartA11yLabel(weightHistory.history, weightHistory.unit)
        : null,
    [weightHistory],
  );

  // No tracked weight yet — bail rather than render an empty card
  if (!weightTracking?.current?.value) return null;

  const current = weightTracking.current;
  const progress = weightTracking.progress; // {value, unit} delta from start
  const goal = weightGoal;

  // Direction: progress.value < 0 means losing weight (good for fat-loss goal).
  // Render arrow + magnitude.
  const lostWeight = progress?.value != null && progress.value < 0;
  const gainedWeight = progress?.value != null && progress.value > 0;
  const noChange = !lostWeight && !gainedWeight;
  const deltaMagnitude = progress?.value != null ? Math.abs(progress.value) : 0;
  let deltaColor = colors.foregroundSecondary;
  let DeltaIcon: typeof ArrowBottomIcon | null = null;
  if (lostWeight) {
    deltaColor = colors.positive;
    DeltaIcon = ArrowBottomIcon;
  } else if (gainedWeight) {
    deltaColor = colors.warning;
    DeltaIcon = ArrowTopIcon;
  }

  // "X lbs to go" — weightGoal.value minus current; positive when goal is
  // below current (typical fat-loss case), zero/negative skips the line.
  const lbsToGo = goal && current ? Math.abs(current.value - goal.value) : null;

  return (
    <View
      style={[
        styles.card,
        { backgroundColor: colors.tertiary, borderColor: colors.secondary },
      ]}
    >
      <View style={styles.row}>
        {/* Current weight */}
        <View style={{ flex: 1 }}>
          <Text style={[styles.label, { color: colors.foregroundSecondary }]}>
            WEIGHT
          </Text>
          <View style={styles.valueRow}>
            <Text style={[styles.bigValue, { color: colors.foreground }]}>
              {current.value.toFixed(1)}
            </Text>
            <Text style={[styles.unit, { color: colors.foregroundSecondary }]}>
              {displayUnit(current.unit)}
            </Text>
          </View>
          {!noChange && DeltaIcon != null && (
            <View style={styles.deltaRow}>
              <DeltaIcon size={12} color={deltaColor} />
              <Text style={[styles.deltaText, { color: deltaColor }]}>
                {deltaMagnitude.toFixed(1)} {displayUnit(current.unit)}{' '}
                {lostWeight ? 'lost' : 'gained'}
              </Text>
            </View>
          )}
        </View>

        {/* Goal */}
        {goal && (
          <View style={styles.goalCol}>
            <Text style={[styles.label, { color: colors.foregroundSecondary }]}>
              GOAL
            </Text>
            <View style={styles.goalValueRow}>
              <Text style={[styles.goalValue, { color: colors.positive }]}>
                {goal.value.toFixed(0)}
              </Text>
              <Text
                style={[styles.unit, { color: colors.foregroundSecondary }]}
              >
                {displayUnit(goal.unit)}
              </Text>
            </View>
            {lbsToGo != null && lbsToGo > 0 && (
              <Text
                style={[styles.toGo, { color: colors.foregroundSecondary }]}
              >
                {lbsToGo.toFixed(1)} {displayUnit(current.unit)} to go
              </Text>
            )}
          </View>
        )}
      </View>

      {/* Inline 7-day weight chart. Renders only when the server returned
          enough non-null history to draw a line — single point or empty
          history collapses the chart slot completely (no marginTop, no
          minHeight, no empty View) rather than showing a flat line or
          reserving 88px of dead space. */}
      {chartPaths ? (
        <View
          onLayout={handleChartLayout}
          style={styles.chartContainer}
          accessible
          accessibilityRole="image"
          accessibilityLabel={chartA11yLabel ?? undefined}
        >
          <Svg width={chartWidth} height={CHART_HEIGHT}>
            <Defs>
              <LinearGradient id="weightArea" x1="0" y1="0" x2="0" y2="1">
                <Stop
                  offset="0"
                  stopColor={colors.positive}
                  stopOpacity="0.3"
                />
                <Stop offset="1" stopColor={colors.positive} stopOpacity="0" />
              </LinearGradient>
            </Defs>
            {chartPaths.goalY != null && (
              <Path
                d={`M${CHART_PAD_X} ${chartPaths.goalY} L${chartWidth - CHART_PAD_X} ${chartPaths.goalY}`}
                stroke={colors.positive}
                strokeOpacity={0.4}
                strokeWidth={1}
                strokeDasharray="4 4"
              />
            )}
            <Path d={chartPaths.area} fill="url(#weightArea)" />
            <Path
              d={chartPaths.line}
              stroke={colors.positive}
              strokeWidth={2}
              fill="none"
            />
            {chartPaths.projection && (
              <Path
                d={chartPaths.projection}
                stroke={colors.positive}
                strokeOpacity={0.5}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                fill="none"
              />
            )}
            {chartPaths.dots.map((dot, idx) => (
              <Circle
                key={idx}
                cx={dot.x}
                cy={dot.y}
                r={3}
                fill={colors.positive}
              />
            ))}
          </Svg>
        </View>
      ) : (
        // Hidden layout-measurement target so we can size the chart on
        // first render once enough data arrives — but with zero visual
        // footprint when the chart is absent.
        <View
          onLayout={handleChartLayout}
          style={styles.chartLayoutMeasure}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 16,
    padding: theme.spacing.m,
  },
  chartContainer: {
    marginTop: theme.spacing.s,
    height: CHART_HEIGHT,
  },
  chartLayoutMeasure: {
    height: 0,
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  label: {
    ...theme.text.xs,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: theme.spacing.xs,
    marginTop: 2,
  },
  bigValue: {
    ...theme.text['3xl'],
    fontWeight: '900',
  },
  unit: {
    ...theme.text.s,
    fontWeight: '500',
  },
  deltaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginTop: 2,
  },
  deltaText: {
    ...theme.text.s,
    fontWeight: '600',
  },
  goalCol: {
    alignItems: 'flex-end',
  },
  goalValueRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 3,
    marginTop: 2,
  },
  goalValue: {
    ...theme.text.l,
    fontWeight: '800',
  },
  toGo: {
    ...theme.text.xs,
    marginTop: 2,
  },
});
